import { ol_Extent, ol_Feature, ol_style } from "@omnia/exports/openlayers"
import { setOpacity } from "../../color/color"
import { ICropping } from "../../fields"
import { convertGeometryToOpenLayers } from "../../geom"
import { getExtentsUnion } from "../../map/utils"
import { createIndex } from "../../utils/array"
import { Datalayer } from "../datalayer"
import { DatalayerZone } from "../datalayerZone"
import { IDataLayer, IDatalayerContext, IDataLayerZone } from "../types"
import { DrawingFeature } from "./feature"
import { getDrawingLineStyle } from "./styles"

export class DrawingLayer<TFeature extends DrawingFeature = DrawingFeature> extends Datalayer {

    protected _fieldZones: TFeature[]

    protected _fieldZonesExtent: {
        extent: ol_Extent
        ids: number[]
    }

    constructor(datalayer: IDataLayer, _context: IDatalayerContext) {
        super(datalayer, _context, {
            labelOptions: {
                declutter: false
            }
        })
        this._fieldZones = this._fieldZones || []
    }

    protected setupZones() {
        const zones = super.createZones().map(z => <TFeature>z)
        this.setupFieldZones(zones)
    }

    protected setupFieldZones(zones: TFeature[]) {
        this._zones = zones.filter(z => !z.isFieldFeature())
        this._fieldZones = zones.filter(z => z.isFieldFeature())
    }

    get clipFeatures() {
        return this.datalayer.ClipFeatures;
    }

    set clipFeatures(value: boolean) {
        this.datalayer.ClipFeatures = value;
    }

    protected createZoneInstance(zone: IDataLayerZone): DatalayerZone {
        return new DrawingFeature(this, zone);
    }

    protected getZoneBaseStyle(feature: ol_Feature, zone: DatalayerZone, selected: boolean, resolution: number): ol_style.Style[] {

        if (zone instanceof DrawingFeature) {

            if (zone.isText()) {
                return []
            }

            if (zone.isLine()) {
                return getDrawingLineStyle(feature, zone, selected)
            }

            if (zone.isPoint()) {
                return zone.getZonePointStyle(selected, resolution)
            }

        }

        return super.getZoneBaseStyle(feature, zone, selected, resolution)

    }

    getZoneFillColor(feature: ol_Feature, zone: DatalayerZone) {

        if (zone instanceof DrawingFeature) {
            let color = zone.getZoneFillColor();
            return color;
        }

        return super.getZoneFillColor(feature, zone);

    }


    getFeatureVisibility(feature: ol_Feature) {

        const data = feature.get("data")

        if ((data instanceof DrawingFeature) && data.isFieldFeature()) {
            return false
        }

        return super.getFeatureVisibility(feature)

    }

    getTooltipTemplate(feature: ol_Feature, data: any, editMode: boolean): string {

        if (!editMode) {
            return super.getTooltipTemplate(feature, data, editMode);
        }

        return undefined;
    }

    checkResolutionLabelFilter(resolution, maxResolution = 10) {
        return (resolution <= maxResolution)
    }


    getFieldZones() {
        return this._fieldZones.slice()
    }

    hasFieldColors() {
        return this._fieldZones.some(f => !!f.fillColor)
    }

    getFieldColors() {

        const fields = this._fieldZones.filter(f => !!f.fillColor)

        if (!fields.length) return null

        const colors = fields.reduce((c, f) => {
            c[f.fieldId] = setOpacity(f.fillColor, 1)
            return c
        }, {})

        return colors
    }


    private getFieldZonesExtent() {

        if (!this._fieldZones) {
            return undefined
        }

        const fieldZones = this._fieldZones.filter(f => !!f.fillColor).map(f => f.fieldId)

        const index = createIndex(fieldZones, id => id)

        const current = this._fieldZonesExtent

        if (current && (current.ids.length == fieldZones.length)) {

            if (current.ids.every(id => index[id])) {
                return current.extent
            }

        }

        const fields = (this._context.getAllFields() || []).filter(f => index[f.fieldId])

        const fieldZoneExtents = fields
            .map(f => convertGeometryToOpenLayers(f.getBoundary()))
            .filter(g => g)
            .map(g => g.getExtent())

        const extent = getExtentsUnion(fieldZoneExtents)

        this._fieldZonesExtent = {
            extent,
            ids: fieldZones
        }

        return extent

    }

    getExtent() {

        let extent = super.getExtent()

        const fields = this.getFieldZonesExtent()

        if (fields) {
            extent = getExtentsUnion([extent, fields])
        }

        return extent

    }

    copyZonesFromCropping(cropping: ICropping[]) {

        super.copyZonesFromCropping(cropping)

        for (const z of this.zones) {

            if (z instanceof DrawingFeature) {
                z.fillColor = [255, 255, 255]
            }

        }

        this.refresh()

    }


}
